"use client";

import React, { useState } from "react";

/** Shared chrome for Zoom / AI Edit / Save overlay pills */
export const galleryActionPillClassName =
  "inline-flex shrink-0 items-center gap-1.5 h-9 px-3.5 rounded-full text-sm font-medium leading-none whitespace-nowrap text-white bg-black/30 backdrop-blur-md shadow-lg transition-colors hover:bg-black/70";

interface SaveButtonProps {
  fileId: string;
  initialSaved?: boolean;
  onSaveComplete?: (saved: boolean) => void;
  className?: string;
}

export default function SaveButton({
  fileId,
  initialSaved = false,
  onSaveComplete,
  className = "",
}: SaveButtonProps) {
  const [saved, setSaved] = useState(initialSaved);
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    if (loading) return;

    setLoading(true);

    try {
      if (saved) {
        const response = await fetch(`/api/storage/save-image?fileId=${fileId}`, {
          method: "DELETE",
        });

        if (!response.ok) {
          throw new Error("Failed to unsave image");
        }

        setSaved(false);
        onSaveComplete?.(false);
      } else {
        const response = await fetch("/api/storage/save-image", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ fileId }),
        });

        if (!response.ok) {
          throw new Error("Failed to save image");
        }

        setSaved(true);
        onSaveComplete?.(true);

        window.dispatchEvent(new Event("refreshAssets"));
      }
    } catch (error) {
      console.error("Error saving/unsaving image:", error);
      alert("Failed to update image. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleSave}
      disabled={loading}
      className={`
        ${galleryActionPillClassName}
        ${loading ? "opacity-70 cursor-wait" : "cursor-pointer"}
        disabled:opacity-70 disabled:cursor-not-allowed
        ${className}
      `}
    >
      <svg
        className={`w-4 h-4 shrink-0 transition-all ${loading ? "animate-pulse" : ""} ${
          saved
            ? "fill-yellow-400 stroke-yellow-400"
            : "fill-none stroke-white group-hover:fill-yellow-400 group-hover:stroke-yellow-400"
        }`}
        strokeWidth={2}
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
        />
      </svg>
      <span>{loading ? "Saving..." : saved ? "Saved!" : "Save"}</span>
    </button>
  );
}
